import type { BoardUser } from './leaderboard.dto.js';

/**
 * Only the fields the leaderboard shows. A server user carries 16 (fbId, devToDevId, pl,
 * loginAt...); copying the needed ones lets the raw response be collected.
 */
export function compactUsers( users: readonly BoardUser[] ): BoardUser[]
{
  return users.map( u => ( {
    userId: u.userId,
    deviceId: u.deviceId ?? null,
    country: u.country ?? '',
    name: u.name ?? null,
    level: u.level ?? 1,
    coins: 0,
    avatar: u.avatar ?? 0,
    fbUrl: u.fbUrl ?? null,
    isOnline: false,
    ratingScore: u.ratingScore ?? 0,
    ratingPosition: u.ratingPosition ?? 0,
    createdAt: u.createdAt ?? null,
  } ) );
}

/**
 * Only the top of a short board. Below the top the server appends a window around the current
 * player (#508, #509, #510...) ranked another way than the board's dense ranks: the same player is
 * #510 there and #101 in the full board, so the player card would flip when the full board replaced
 * the head. The top is the prefix where the rank grows by at most 1 per row; the full board brings
 * the player's neighbourhood with the right ranks a moment later.
 */
export function headTop( users: readonly BoardUser[] ): BoardUser[]
{
  let end = 0;
  while ( end < users.length
    && ( end === 0 || ( users[end].ratingPosition ?? 0 ) - ( users[end - 1].ratingPosition ?? 0 ) <= 1 ) )
    end++;
  return users.slice( 0, end );
}

/** One user as a tuple: half the size of the object form in localStorage. */
type PackedUser = [
  number,         // userId
  string | null,  // deviceId
  string | null,  // name
  number,         // level
  number,         // avatar
  string | null,  // fbUrl
  number,         // ratingScore
  number,         // ratingPosition
  string,         // country
  string | null,  // createdAt
];

export interface PersistedBoard
{
  v: 1;
  /** When the board was fetched (ms). */
  at: number;
  /** Leaderboard base URL: a QA environment override must not show the other backend's board. */
  env: string;
  users: PackedUser[];
}

export function packBoard( users: readonly BoardUser[], at: number, env: string ): PersistedBoard
{
  return {
    v: 1,
    at,
    env,
    users: users.map( u => [
      u.userId, u.deviceId ?? null, u.name ?? null, u.level ?? 1, u.avatar ?? 0,
      u.fbUrl ?? null, u.ratingScore ?? 0, u.ratingPosition ?? 0, u.country ?? '', u.createdAt ?? null,
    ] ),
  };
}

/** The persisted board, or null when it is missing, malformed, from another backend or too old. */
export function unpackBoard(
  raw: unknown,
  env: string,
  now: number,
  max_age_ms: number,
): { users: BoardUser[]; at: number } | null
{
  if ( !raw || typeof raw !== 'object' ) return null;
  const board = raw as Partial<PersistedBoard>;
  if ( board.v !== 1 || board.env !== env || typeof board.at !== 'number' || !Array.isArray( board.users ) )
    return null;
  if ( now - board.at > max_age_ms || board.at > now + 60_000 )
    return null;

  const users: BoardUser[] = [];
  for ( const p of board.users )
  {
    if ( !Array.isArray( p ) || p.length < 10 || typeof p[0] !== 'number' || typeof p[7] !== 'number' )
      return null;
    users.push( {
      userId: p[0],
      deviceId: p[1],
      name: p[2],
      level: p[3],
      coins: 0,
      avatar: p[4],
      fbUrl: p[5],
      isOnline: false,
      ratingScore: p[6],
      ratingPosition: p[7],
      country: p[8],
      createdAt: p[9],
    } );
  }
  return { users, at: board.at };
}
